/**
 * One encounter's event stream, off the admin recent-activity timeline
 * (GDN-2774).
 *
 * The billing worklist page asks this question twice per row — once for the
 * coverage badge in the table, once again when the row is expanded — so both
 * callers share a single hook with identical query args. RTK Query dedupes on
 * those args, so a row costs one request no matter how many times it's read,
 * and expanding a row is free: the entries are already cached.
 *
 * The probe is deliberately capped rather than paginated. This page measures
 * whether events exist at all, not how many; `hasMore` is enough to say "at
 * least N" honestly without a second round trip.
 */
import { useGetRecentActivityQuery } from '../store/hooks';
import type { RecentActivityEntry } from '../store/api';
import { isPhiAccessDenied } from '../utils/phi-access';

/** The envelope-flag lens, matching the recent-activity `category` param. */
export type EventLens = 'all' | 'domain' | 'audit' | 'usage';

/** How many events a single row's probe pulls. See the note above. */
export const EVENT_PROBE_LIMIT = 25;

export interface EncounterEvents {
  entries: RecentActivityEntry[];
  /** Events found, capped at EVENT_PROBE_LIMIT — see `hasMore`. */
  count: number;
  /** True when the encounter has more events than the probe pulled. */
  hasMore: boolean;
  isLoading: boolean;
  /** The load finished and found nothing. The gap this page exists to expose. */
  isEmpty: boolean;
  /** The PHI gate refused this caller. */
  isDenied: boolean;
  /** The timeline failed for any other reason (store down, query error). */
  isFailed: boolean;
}

export const useEncounterEvents = ({
  organizationId,
  encounterId,
  lens,
  skip = false,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
  skip?: boolean;
}): EncounterEvents => {
  const { data, isLoading, isFetching, error } = useGetRecentActivityQuery(
    {
      organizationId,
      encounterId,
      category: lens,
      limit: EVENT_PROBE_LIMIT,
    },
    { skip: skip || !organizationId || !encounterId },
  );

  const entries = data?.entries ?? [];
  const loading = (isLoading || isFetching) && !data;
  const isDenied = isPhiAccessDenied(error);

  return {
    entries,
    count: entries.length,
    hasMore: !!data?.has_more,
    isLoading: loading,
    // Only a completed, successful, non-empty-by-answer load is a real gap —
    // a failure that happens to return no rows must never read as "no events".
    isEmpty: !loading && !error && entries.length === 0,
    isDenied,
    isFailed: !!error && !isDenied,
  };
};
